/**
 * MKS-477 (quick task 261029-fgf) — the Tasks page.
 *
 * Two tabs, the selected one kept in `?tab=` (absent / unknown = My Tasks):
 *   - "My Tasks"  — today's `MyTasksPage`, unchanged (it still owns its own `?tiers=` param,
 *                   which survives tab switches because only `tab` is touched).
 *   - "All tasks" — every project board the user can read that holds at least one live task:
 *                   name, customer, open-task count, last activity and a board link. Sortable by
 *                   name / last activity (default: last activity, newest first), with a
 *                   client-side name filter and finished projects behind "Show finished".
 *
 * Tab idiom borrowed from ManagerOverviewPage (MKS-464).
 *
 * `MyTasksRoute` is the element behind /my-tasks: it redirects to /tasks, keeping the query.
 */
import { useState } from 'react';
import { Link, Navigate, useLocation, useSearchParams } from 'react-router-dom';
import MyTasksPage from './MyTasksPage';
import { useTaskBoards } from '../hooks/useProjects';
import type { TaskBoardSummary } from '../services/api/projects';
import { formatDisplayDateTime } from '../utils/format';

type TasksTab = 'mine' | 'all';

const TABS: Array<{ value: TasksTab; label: string }> = [
  { value: 'mine', label: 'My Tasks' },
  { value: 'all', label: 'All tasks' },
];

// Finished project states, from `ProjectState` in shared/types/project.ts
// ('draft' | 'in_progress' | 'paused' | 'completed' | 'cancelled' | 'archived').
const FINISHED_PROJECT_STATES = ['completed', 'cancelled', 'archived'];

type SortKey = 'lastActivity' | 'name';
type SortDir = 'asc' | 'desc';

export default function TasksPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab: TasksTab = searchParams.get('tab') === 'all' ? 'all' : 'mine';

  function selectTab(next: TasksTab) {
    const params = new URLSearchParams(searchParams);
    if (next === 'mine') params.delete('tab');
    else params.set('tab', next);
    setSearchParams(params, { replace: true });
  }

  return (
    <div className="bg-ms-canvas flex-1 flex flex-col overflow-hidden">
      <div className="shrink-0 px-7 pt-4">
        <div
          className="inline-flex bg-ms-surface-2 rounded-md p-0.5 border border-ms-border-subtle"
          role="tablist"
          aria-label="Tasks view"
        >
          {TABS.map((opt) => {
            const active = opt.value === tab;
            const cls = active
              ? 'bg-ms-surface text-ms-text-strong shadow-sm'
              : 'text-ms-text-secondary hover:text-ms-text-strong';
            return (
              <button
                key={opt.value}
                type="button"
                role="tab"
                aria-selected={active}
                className={`px-2.5 py-1 rounded text-[11.5px] font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-ms-orange/40 ${cls}`}
                onClick={() => selectTab(opt.value)}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>
      {tab === 'all' ? (
        <div className="flex-1 overflow-auto px-7 py-5">
          <AllTasksTab />
        </div>
      ) : (
        <MyTasksPage />
      )}
    </div>
  );
}

function displayName(row: TaskBoardSummary): string {
  return row.fullName ?? row.name ?? '(unnamed)';
}

function compareRows(a: TaskBoardSummary, b: TaskBoardSummary, key: SortKey, dir: SortDir): number {
  if (key === 'name') {
    const c = displayName(a).localeCompare(displayName(b), undefined, { sensitivity: 'base' });
    return dir === 'asc' ? c : -c;
  }
  // Last activity: ISO strings compare chronologically; rows without any activity sort last in
  // both directions.
  if (a.lastActivityAt === null && b.lastActivityAt === null) return 0;
  if (a.lastActivityAt === null) return 1;
  if (b.lastActivityAt === null) return -1;
  const c = a.lastActivityAt.localeCompare(b.lastActivityAt);
  return dir === 'asc' ? c : -c;
}

function AllTasksTab() {
  const { data, isLoading, isError } = useTaskBoards();
  const [filter, setFilter] = useState('');
  const [showFinished, setShowFinished] = useState(false);
  const [sort, setSort] = useState<{ key: SortKey; dir: SortDir }>({
    key: 'lastActivity',
    dir: 'desc',
  });

  function toggleSort(key: SortKey) {
    setSort((cur) =>
      cur.key === key
        ? { key, dir: cur.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: key === 'name' ? 'asc' : 'desc' },
    );
  }

  const needle = filter.trim().toLowerCase();
  const rows = (data ?? [])
    .filter((r) => showFinished || !FINISHED_PROJECT_STATES.includes(r.state ?? ''))
    .filter((r) => needle === '' || displayName(r).toLowerCase().includes(needle))
    .sort((a, b) => compareRows(a, b, sort.key, sort.dir));

  const ariaSort = (key: SortKey) =>
    sort.key !== key ? 'none' : sort.dir === 'asc' ? 'ascending' : 'descending';
  const arrow = (key: SortKey) => (sort.key !== key ? '' : sort.dir === 'asc' ? ' ↑' : ' ↓');

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-4">
        <input
          type="text"
          aria-label="Filter projects"
          placeholder="Filter projects"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="w-64 rounded-md border border-ms-border-subtle bg-ms-surface px-2.5 py-1.5 text-[12.5px] text-ms-text-strong focus:outline-none focus:ring-2 focus:ring-ms-orange/40"
        />
        <label className="flex items-center gap-1.5 text-[12.5px] text-ms-text-secondary">
          <input
            type="checkbox"
            checked={showFinished}
            onChange={(e) => setShowFinished(e.target.checked)}
          />
          Show finished
        </label>
      </div>

      {isLoading ? (
        <p className="text-[12.5px] text-ms-text-secondary">Loading boards...</p>
      ) : isError ? (
        <p className="text-[12.5px] text-red-600">Could not load boards.</p>
      ) : rows.length === 0 ? (
        <p className="text-[12.5px] text-ms-text-secondary">No project boards with tasks.</p>
      ) : (
        <div className="overflow-hidden rounded-md border border-ms-border-subtle bg-ms-surface">
          <table className="w-full text-left text-[12.5px]">
            <thead className="bg-ms-surface-2 text-ms-text-secondary">
              <tr>
                <th scope="col" aria-sort={ariaSort('name')} className="px-3 py-2 font-medium">
                  <button type="button" onClick={() => toggleSort('name')}>
                    Project{arrow('name')}
                  </button>
                </th>
                <th scope="col" className="px-3 py-2 font-medium">
                  Customer
                </th>
                <th scope="col" className="px-3 py-2 font-medium">
                  Open tasks
                </th>
                <th
                  scope="col"
                  aria-sort={ariaSort('lastActivity')}
                  className="px-3 py-2 font-medium"
                >
                  <button type="button" onClick={() => toggleSort('lastActivity')}>
                    Last activity{arrow('lastActivity')}
                  </button>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-ms-border-subtle">
                  <td className="px-3 py-2">
                    <Link
                      to={`/projects/${r.id}/board`}
                      className="text-ms-text-strong hover:text-ms-orange hover:underline"
                    >
                      {displayName(r)}
                    </Link>
                  </td>
                  <td className="px-3 py-2 text-ms-text-secondary">{r.customerName ?? '-'}</td>
                  <td className="px-3 py-2 text-ms-text-secondary">{r.openTaskCount}</td>
                  <td className="px-3 py-2 text-ms-text-secondary">
                    {formatDisplayDateTime(r.lastActivityAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/**
 * The element behind /my-tasks: redirect to /tasks, keeping the query.
 */
export function MyTasksRoute() {
  const location = useLocation();
  return <Navigate to={{ pathname: '/tasks', search: location.search }} replace />;
}
